import React from 'react'

/* Form & Form Validation */
import * as Yup from 'yup'

import { Form, Input, Select, Checkbox } from 'formik-antd'
import { Divider } from 'antd'

import { InputDateTime } from '../../../../core/components/form'

import { DATETIME_FORMAT } from '../../../../core/utils/i18n-js'

import { getDateTimeApiFormat, getTimezone, createDate, nowWithTimezone } from '../../../../core/utils/timezones'
import ModalForm from '../../../../core/components/form/modalForm'
import FormServiceSelector from '../../../../components/service/form/selector'
import { isNotValid, translateSymfonyErrorToFormik } from '../../../../core/utils/form'
import { useCreateTask, useUpdateTask } from '../../../../hooks/manager/job'

import { TASK_RUSH_TYPES, TASK_RUSH_TYPE_NONE } from '../../../../config/const'

const ValidationSchema = Yup.object().shape({
  code: Yup.string().required('Cannot be blank.'),
  service: Yup.string().nullable().required('Cannot be blank.'),
  languageType: Yup.string().nullable().required('Cannot be blank.'),
  language: Yup.string().when(['languageType', 'languageFamily'], {
    is: (languageType, languageFamily) =>
      languageType === 'Single' && languageFamily !== 'LTR' && languageFamily !== 'RTL',
    then: Yup.string().required('Cannot be blank')
  }),
  languagePair: Yup.string().when('languageType', {
    is: 'Pair',
    then: (fieldSchema) => fieldSchema.required('Cannot be blank.')
  }),
  isWord: Yup.bool().required(),
  catlog: Yup.string().when('isWord', {
    is: true,
    then: (fieldSchema) => fieldSchema.required('Cannot be blank.')
  }),
  unit: Yup.string().when('isWord', {
    is: false,
    then: (fieldSchema) => fieldSchema.required('Cannot be blank.')
  }),
  amount: Yup.number()
    .typeError('Must be a number')
    .when('isWord', {
      is: false,
      then: (fieldSchema) => fieldSchema.required('Cannot be blank.')
    }),
  deadlineAt: Yup.date().required('Cannot be blank.').typeError('Must be a date')
})

const _filter = (input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0

const catlogs = [
  { value: 1, label: 'XTM Excel' },
  { value: 2, label: 'Salesforce CSV' }
]

const Formulario = (props) => {
  const { task, handleCancel: onCancel, loading, id } = props
  const { mutate: createTask } = useCreateTask(id)
  const { mutate: updateTask } = useUpdateTask(id)

  function handleSubmit (values, bag) {
    const deadlineAtTimezone = getTimezone(values.deadlineAt)
    const deadlineAt = getDateTimeApiFormat(values.deadlineAt)

    const form = {
      ...values,
      deadlineAtTimezone,
      deadlineAt
    }

    let wrapper = {}
    let onSubmit = () => {}

    if (task) {
      wrapper = { taskId: task.id, form }
      onSubmit = updateTask
    } else {
      wrapper = form
      onSubmit = createTask
    }

    onSubmit(wrapper, {
      onSuccess: (data) => {
        bag.setSubmitting(false)
        onCancel()
      },
      onError: (error, variables, context) => {
        bag.setSubmitting(false)
        if (error.response.status === 400) {
          const data = error.response?.data
          if (isNotValid(data)) {
            bag.setErrors(translateSymfonyErrorToFormik(data?.errors?.children))
          }
        }
      }
    })
  }

  const _initialForm = () => {
    if (task) {
      const deadlineAt = createDate(task.deadlineAt, task.deadlineAtTimezone)
      return {
        code: task.code,
        service: task.service.id,
        category: task.serviceCategory?.id,
        language: task.language?.id,
        languageType: task.languageType,
        languageFamily: task.languageFamily,
        languagePair: task.languagePair?.id,
        vendor: task.vendor?.id,
        deadlineAt: deadlineAt,
        isWord: task.isWord,
        catlog: task.isWord ? task.catlog.id : '',
        unit: task.baseUnit ? task.baseUnit.id : '',
        amount: !task.isWord ? task.baseUnitAmount : '',
        notes: task?.notes,
        isFree: task.isFree,
        rushType: task.rushType
      }
    } else {
      return {
        code: '',
        service: '',
        category: '',
        language: '',
        languageFamily: '',
        languageType: 'Pair',
        languagePair: '',
        vendor: '',
        deadlineAt: nowWithTimezone(),
        isWord: false,
        catlog: '',
        unit: '',
        amount: '',
        notes: '',
        isFree: false,
        rushType: TASK_RUSH_TYPE_NONE
      }
    }
  }

  const catlogEnable = (catlogs) => {
    return catlogs.filter((e) => props.catlogs.some((c) => c.id == e.value))
  }

  return (
    <ModalForm
      loading={loading}
      title='Task'
      initialValues={_initialForm()}
      onCancel={onCancel}
      validationSchema={ValidationSchema}
      onSubmit={handleSubmit}
      visible
      width={700}
      render={({ isSubmitting, setFieldValue, touched, errors, values }) => (
        <>
          <Form.Item label='Code' name='code' hasFeedback showValidateSuccess required>
            <Input name='code' placeholder='Code #' />
          </Form.Item>

          <FormServiceSelector formik={{ isSubmitting, touched, setFieldValue, values, errors }} />

          {!values.isWord && (
            <Form.Item label='Unit Amount' name='amount' hasFeedback showValidateSuccess>
              <Input name='amount' placeholder='Amount' />
            </Form.Item>
          )}

          {values.isWord && (
            <Form.Item label='CAT Format' name='catlog' hasFeedback showValidateSuccess>
              <Select
                placeholder='CAT Format'
                name='catlog'
                showSearch
                options={catlogEnable(catlogs)}
                filterOption={_filter}
              />
            </Form.Item>
          )}

          <InputDateTime
            name='deadlineAt'
            label='DDL'
            placeholder='Deadline At'
            errors={errors.deadlineAt}
            touched={touched.deadlineAt}
            ayout='horizontal'
            showTime={{ format: 'HH:mm', minuteStep: 15 }}
            format={DATETIME_FORMAT}
            defaultTimezone={values.deadlineAt ? values.deadlineAt.tz() : null}
            required
          />

          <Form.Item label='Free of Charge' name='isFree' hasFeedback>
            <Checkbox name='isFree' />
          </Form.Item>

          <Form.Item label='Rush' name='rushType' hasFeedback showValidateSuccess>
            <Select placeholder='Rush' name='rushType'>
              {TASK_RUSH_TYPES.map((e) => (
                <Select.Option key={e} value={e}>{e}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label='Notes' name='notes' hasFeedback>
            <Input.TextArea name='notes' placeholder='Notes' />
          </Form.Item>

          <Divider plain />
        </>
      )}
    />
  )
}

export default Formulario
